import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { ClinicProvider } from "@/context/ClinicContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { PermissionProvider } from "@/context/PermissionContext";
import { WebSocketProvider } from "@/context/WebSocketContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ErrorBoundary from "@/components/ErrorBoundary";

// Pages
import ModernLandingPage from "./pages/ModernLandingPage";
import CMSPage from "./pages/CMSPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AdminDashboard from "./pages/AdminDashboard";
import StaffDashboard from "./pages/StaffDashboard";
import PatientDashboard from "./pages/PatientDashboard";
import PatientLoginPage from "./pages/PatientLoginPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import ClinicSignup from "./pages/ClinicSignupEnhanced";
import ClinicRegistration from "./pages/ClinicRegistration";
import AcceptInvitation from "./pages/AcceptInvitation";
import AcceptTeamInvitation from "./pages/AcceptTeamInvitation";
import HelpCenter from "./pages/HelpCenter";
import AIMembershipHelp from "./pages/help/AIMembershipHelp";
import WalkInRegistrationGuide from "./pages/help/WalkInRegistrationGuide";
import HelpArticle from "./pages/help/HelpArticle";
import MarketingGuide from "./components/help/MarketingGuide";
import WidgetGuide from "./components/help/WidgetGuide";
import PricingPage from "./pages/PricingPage";
import HIPAACompliance from "./pages/HIPAACompliance";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import GoogleAuthCallback from "./pages/GoogleAuthCallback";
import EmailVerificationPage from "./pages/EmailVerificationPage";
import VerifyMemberPage from "./pages/VerifyMemberPage";

// Components
import ImpersonationBanner from "./components/ImpersonationBanner";
import IdleTimeoutWarning from "./components/IdleTimeoutWarning";
import SubscriptionGuard from "./components/admin/SubscriptionGuard";
import { useIdleTimeout } from "./hooks/useIdleTimeout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API = `${BACKEND_URL}/api`;

// Debug logging for production issues
if (typeof window !== 'undefined') {
  console.log('App initialized with BACKEND_URL:', BACKEND_URL || '(empty - using relative paths)');
}

// Track redirect attempts to prevent loops
let redirectAttempts = 0;
const MAX_REDIRECT_ATTEMPTS = 1;

// Set up axios defaults with multi-tenant support
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Add clinic ID header for multi-tenancy (only if clinic_id exists)
  const clinicId = localStorage.getItem('clinic_id');
  if (clinicId && clinicId !== 'null' && clinicId !== 'undefined') {
    config.headers['X-Clinic-ID'] = clinicId;
  }
  
  return config;
});

axios.interceptors.response.use(
  (response) => {
    // Reset redirect attempts on successful response
    redirectAttempts = 0;
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const requestUrl = error.config?.url || '';
    
    // Handle network errors (CORS, connection refused, etc.) - don't logout
    if (!error.response) {
      console.warn('Network error (possibly CORS):', requestUrl);
      // Don't logout for network errors - user might just have connectivity issues
      return Promise.reject(error);
    }
    
    // Handle rate limiting (429)
    if (status === 429) {
      console.warn('Rate limited by server. Please wait before retrying.');
      return Promise.reject(error);
    }
    
    // Handle unauthorized (401) - but be very careful about auto-logout
    if (status === 401) {
      // Don't auto-redirect on certain pages or if already on login
      const currentPath = window.location.pathname;
      const excludedPaths = ['/payment-success', '/login', '/register', '/register-patient', '/register-clinic', '/join/', '/forgot-password', '/reset-password', '/verify', '/accept-invitation', '/page/'];
      const isExcluded = excludedPaths.some(path => currentPath.includes(path));
      
      // Don't logout for these endpoints - they can fail silently without affecting user session
      const silentFailEndpoints = [
        '/my-permissions',
        '/notifications', 
        '/clinic/branding', 
        '/clinic/profile',
        '/clinic/info',
        '/dashboard/stats',
        '/audit-logs',
        '/advanced-reports',
        '/reports/',
        '/analytics',
        '/onboarding-status',
        '/members',
        '/plans',
        '/appointments',
        '/payments',
        '/clinic-subscription'
      ];
      const isSilentFail = silentFailEndpoints.some(endpoint => requestUrl.includes(endpoint));
      
      if (isSilentFail) {
        console.warn('Auth error on non-critical endpoint, not logging out:', requestUrl);
        return Promise.reject(error);
      }
      
      // Only logout for critical auth failures (login, token refresh, etc.)
      const criticalAuthEndpoints = ['/auth/login', '/auth/refresh', '/auth/verify'];
      const isCriticalAuth = criticalAuthEndpoints.some(endpoint => requestUrl.includes(endpoint));
      
      if (isCriticalAuth && !isExcluded && redirectAttempts < MAX_REDIRECT_ATTEMPTS) {
        // Only redirect if we have a token (meaning session expired)
        const hasToken = localStorage.getItem('token');
        if (hasToken) {
          redirectAttempts++;
          console.warn('Session expired on critical endpoint, redirecting to login');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('clinic_id');
          // Use replace to prevent back button issues
          window.location.replace('/login');
        }
      }
    }
    
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonationData, setImpersonationData] = useState(null);

  // Handle logout (used by idle timeout and manual logout)
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('clinic_id');
    localStorage.removeItem('impersonation_data');
    localStorage.removeItem('original_admin_token');
    localStorage.removeItem('original_admin_user');
    setUser(null);
    setImpersonationData(null);
    window.location.href = '/login?reason=idle';
  };

  // Idle timeout hook - only active when user is logged in
  // Pass null to let the hook use its internal settings.enabled
  const { 
    showWarning, 
    remainingTime, 
    continueSession,
    isEnabled: idleTimeoutEnabled
  } = useIdleTimeout(handleLogout, user ? null : false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const savedImpersonation = localStorage.getItem('impersonation_data');
    const savedPatientImpersonation = localStorage.getItem('patient_impersonation_data');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    
    // Check for patient impersonation first (it takes precedence)
    if (savedPatientImpersonation) {
      try {
        setImpersonationData(JSON.parse(savedPatientImpersonation));
      } catch (e) {
        localStorage.removeItem('patient_impersonation_data');
      }
    } else if (savedImpersonation) {
      try {
        setImpersonationData(JSON.parse(savedImpersonation));
      } catch (e) {
        localStorage.removeItem('impersonation_data');
      }
    }
    
    setLoading(false);
  }, []);

  const handleStopImpersonation = (response) => {
    setImpersonationData(null);
    if (response?.user) {
      setUser(response.user);
    }
  };

  const ProtectedRoute = ({ children, allowedRoles }) => {
    if (loading) return <div>Loading...</div>;
    if (!user) return <Navigate to="/login" />;
    if (allowedRoles && !allowedRoles.includes(user.role)) {
      return <Navigate to="/" />;
    }
    return children;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  // Only provide WebSocket for authenticated users
  const WebSocketWrapper = ({ children }) => {
    if (user) {
      return <WebSocketProvider>{children}</WebSocketProvider>;
    }
    return <>{children}</>;
  };

  return (
    <ThemeProvider>
    <ClinicProvider>
      <BrandingProvider>
        <PermissionProvider user={user}>
          <WebSocketWrapper>
          <div className="App" style={{ fontFamily: 'var(--font-body, Inter, sans-serif)' }}>
            {/* Impersonation Banner - shown when super admin is impersonating */}
            {impersonationData && (
              <ImpersonationBanner 
                impersonationData={impersonationData}
                onStopImpersonation={handleStopImpersonation}
              />
            )}
            
            {/* Idle Timeout Warning Modal */}
            <IdleTimeoutWarning
              isOpen={showWarning}
              remainingTime={remainingTime}
              onContinue={continueSession}
              onLogout={handleLogout}
            />
            
            {/* Add top padding when impersonation banner is shown */}
            <div className={impersonationData ? 'pt-12' : ''}>
            <Toaster position="top-right" richColors />
            <BrowserRouter>
              <Routes>
                <Route path="/" element={user ? <Navigate to={`/${['super_admin', 'super_owner', 'platform_admin', 'support_admin', 'content_admin'].includes(user.role) ? 'super-admin' : (user.role === 'patient' ? 'patient' : (['staff', 'nurse', 'provider'].includes(user.role) ? 'staff' : 'admin'))}`} /> : <ModernLandingPage />} />
                <Route path="/pricing" element={<PricingPage />} />
              
              {/* CMS Pages with friendly URLs */}
              <Route path="/privacy-policy" element={<CMSPage />} />
              <Route path="/terms-of-service" element={<CMSPage />} />
            <Route path="/about" element={<CMSPage />} />
            <Route path="/contact" element={<CMSPage />} />
            <Route path="/security" element={<CMSPage />} />
            <Route path="/documentation" element={<CMSPage />} />
            <Route path="/blog" element={<CMSPage />} />
            <Route path="/careers" element={<CMSPage />} />
            
            {/* Redirects from old URLs to friendly URLs */}
            <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />
            <Route path="/page/privacy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/page/privacy-policy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/page/terms" element={<Navigate to="/terms-of-service" replace />} />
            <Route path="/page/terms-of-service" element={<Navigate to="/terms-of-service" replace />} />
            <Route path="/page/about" element={<Navigate to="/about" replace />} />
            <Route path="/page/contact" element={<Navigate to="/contact" replace />} />
            <Route path="/page/security" element={<Navigate to="/security" replace />} />
            <Route path="/page/api-docs" element={<Navigate to="/documentation" replace />} />
            <Route path="/page/documentation" element={<Navigate to="/documentation" replace />} />
            <Route path="/api-docs" element={<Navigate to="/documentation" replace />} />
            <Route path="/page/blog" element={<Navigate to="/blog" replace />} />
            <Route path="/page/careers" element={<Navigate to="/careers" replace />} />
            <Route path="/pages/:slug" element={<CMSPage />} />
            
            <Route path="/hipaa-compliance" element={<CMSPage />} />
            <Route path="/business-associate-agreement" element={<CMSPage />} />
            <Route path="/press" element={<CMSPage />} />
            <Route path="/cookies" element={<CMSPage />} />
            <Route path="/clinic-signup" element={<Navigate to="/register-clinic" replace />} />
            <Route path="/register-clinic" element={<ClinicRegistration />} />
            <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
            <Route path="/accept-team-invitation" element={<AcceptTeamInvitation />} />
            
            {/* Public Membership Verification - QR Code scan destination */}
            <Route path="/verify/:patientId" element={<VerifyMemberPage />} />
            
            {/* Patient Portal Login - Clinic-specific URL */}
            <Route path="/patient/:clinicSubdomain" element={<PatientLoginPage setUser={setUser} />} />
            <Route path="/portal/:clinicSubdomain" element={<PatientLoginPage setUser={setUser} />} />
            
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/help/marketing" element={<MarketingGuide />} />
            <Route path="/help/widget" element={<WidgetGuide />} />
            <Route path="/help/walk-in-registration" element={<WalkInRegistrationGuide />} />
            <Route path="/help/ai-suggestions" element={<AIMembershipHelp />} />
            <Route path="/help/:slug" element={<HelpArticle />} />
            <Route path="/page/:slug" element={<CMSPage />} />
            <Route path="/login" element={user ? <Navigate to={`/${['super_admin', 'super_owner', 'platform_admin', 'support_admin', 'content_admin'].includes(user.role) ? 'super-admin' : (user.role === 'patient' ? 'patient' : (['staff', 'receptionist', 'nurse', 'provider'].includes(user.role) ? 'staff' : 'admin'))}`} /> : <LoginPage setUser={setUser} />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<EmailVerificationPage />} />
            <Route path="/auth/google/callback" element={<GoogleAuthCallback setUser={setUser} />} />
            {/* Main /register now redirects to clinic registration (B2B priority) */}
            <Route path="/register" element={user ? <Navigate to={`/${user.role === 'patient' ? 'patient' : 'admin'}`} /> : <Navigate to="/register-clinic" replace />} />
            {/* Patient registration - accessed via clinic-specific links or direct URL */}
            <Route path="/register-patient" element={user ? <Navigate to="/patient" /> : <RegisterPage />} />
            {/* Friendly patient registration URL: /join/clinic-slug */}
            <Route path="/join/:clinicSlug" element={user ? <Navigate to="/patient" /> : <RegisterPage />} />
            
            {/* Preview route for admins - always shows registration page */}
            <Route path="/preview-join/:clinicSlug" element={<RegisterPage />} />
            {/* Clinic selector is now only accessible by super admin - redirect others to login */}
            <Route path="/select-clinic" element={<Navigate to="/login" />} />
            
            <Route
              path="/super-admin/*"
              element={
                <ProtectedRoute allowedRoles={["super_admin", "super_owner", "platform_admin", "support_admin", "content_admin"]}>
                  <ErrorBoundary>
                    <SuperAdminDashboard user={user} setUser={setUser} />
                  </ErrorBoundary>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute allowedRoles={["admin", "clinic_admin", "manager", "receptionist"]}>
                  <ErrorBoundary>
                    <SubscriptionGuard>
                      <AdminDashboard user={user} setUser={setUser} />
                    </SubscriptionGuard>
                  </ErrorBoundary>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/staff/*"
              element={
                <ProtectedRoute allowedRoles={["staff", "receptionist", "nurse", "provider"]}>
                  <StaffDashboard user={user} setUser={setUser} />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/patient/*"
              element={
                <ProtectedRoute allowedRoles={["patient"]}>
                  <ErrorBoundary>
                    <PatientDashboard user={user} setUser={setUser} />
                  </ErrorBoundary>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/patient/payment-success"
              element={
                <ProtectedRoute allowedRoles={["patient"]}>
                  <PaymentSuccess />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/payment-success"
              element={
                <ProtectedRoute allowedRoles={["patient", "admin"]}>
                  <PaymentSuccess />
                </ProtectedRoute>
              }
            />
          </Routes>
        </BrowserRouter>
            </div>
      </div>
          </WebSocketWrapper>
        </PermissionProvider>
      </BrandingProvider>
    </ClinicProvider>
    </ThemeProvider>
  );
}

export default App;
